import { useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

import MyMiniLeagueCard from '@components/my-mini-league/MyMiniLeagueCard';
import { useAuth0User } from '@hooks/useAuth0User';
import { buildLoginHref } from '@lib/auth0/login-url';
import { WEBSITE_NAME } from '@lib/constants';

const MY_MINI_LEAGUE_PATH = '/my-mini-league';

// Matches routes.logout in lib/auth0.ts, which cannot be imported here: it
// pulls in the server-only Auth0Client.
const LOGOUT_PATH = '/auth/logout';

// The site header is fixed and reserves its own spacer of this height, so the
// page fills the rest of the viewport and the footer lands just below the
// fold on a short card rather than mid-screen.
const FIXED_HEADER_HEIGHT_PX = 74;

/**
 * Direct landing for the Mini-League Promise (FFH-2732), linked from the
 * email campaign. Signed-in members set or change their nomination here;
 * anyone else is sent through login and returned, because unlike
 * /funnel-welcome the page has nothing to show without the profile write.
 */
const MyMiniLeague = () => {
  const router = useRouter();
  const { user, isLoading } = useAuth0User();
  const loginHref = buildLoginHref(MY_MINI_LEAGUE_PATH);

  useEffect(() => {
    if (!isLoading && !user) {
      router.replace(loginHref);
    }
  }, [isLoading, user, router, loginHref]);

  return (
    <div
      className="bg-neutral-100"
      style={{ minHeight: `calc(100vh - ${FIXED_HEADER_HEIGHT_PX}px)` }}
    >
      <Head>
        <title>{`My mini-league | ${WEBSITE_NAME}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <main className="mx-auto w-full max-w-[560px] px-6 pt-14 pb-20">
        {user ? (
          <>
            <MyMiniLeagueCard />
            <p className="mt-5 text-center font-open-sans text-[13px] text-neutral-500">
              {`Signed in as ${user.email} · `}
              <Link
                href={LOGOUT_PATH}
                className="text-neutral-500 underline hover:text-neutral-700"
              >
                Not you?
              </Link>
            </p>
          </>
        ) : (
          <div className="h-40 animate-pulse rounded-2xl border border-neutral-200 bg-white" />
        )}
      </main>
    </div>
  );
};

export default MyMiniLeague;
